import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Camera, Play } from "lucide-react";
import { photoUrl, posterUrl, isVideo } from "@/lib/api";

export const VideoTile = ({ p, className = "" }) => (
  <div className={`relative bg-forest ${className}`}>
    {p.poster ? (
      <img decoding="async" src={posterUrl(p)} alt={p.caption || "Video"} loading="lazy" className="block h-auto w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
    ) : (
      <video src={`${photoUrl(p)}#t=0.5`} preload="metadata" muted playsInline className="block h-auto w-full object-contain" />
    )}
    <div className="absolute inset-0 flex items-center justify-center bg-ink/20 transition-colors duration-300 group-hover:bg-ink/35">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cream/90 text-forest shadow-xl transition-transform duration-300 group-hover:scale-110"><Play className="ml-0.5 h-6 w-6 fill-current" /></span>
    </div>
    <span className="absolute left-3 top-3 rounded-full bg-terra px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-cream">Video</span>
  </div>
);

const initialsOf = (s = "") => s.replace(/[^a-zA-Z ]/g, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export const EmptyFrame = ({ ratio = "aspect-[4/5]", label, initials, icon: Icon = Camera, className = "", rounded = "rounded-3xl", testid }) => (
  <div
    data-testid={testid || "empty-frame"}
    className={`empty-frame relative flex flex-col items-center justify-center overflow-hidden ${rounded} ${ratio} ${className}`}
    aria-hidden="true"
  >
    {initials ? (
      <span className="font-display text-3xl font-bold tracking-tight text-cream/90 sm:text-4xl">{initialsOf(initials)}</span>
    ) : (
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-cream/10 ring-1 ring-cream/20">
        <Icon className="h-6 w-6 text-amber" strokeWidth={1.4} />
      </div>
    )}
    {label && <p className="relative mt-4 px-6 text-center font-display text-xs font-semibold tracking-wide text-cream/70 sm:text-sm">{label}</p>}
  </div>
);

export const PhotoFrame = ({ photo, ratio = "aspect-[4/5]", alt = "", className = "", rounded = "rounded-3xl", label, initials, icon, onClick, priority = false, testid }) => {
  if (!photo) return <EmptyFrame ratio={ratio} label={label} initials={initials} icon={icon} className={className} rounded={rounded} testid={testid} />;
  return (
    <div
      data-testid={testid || `photo-${photo.section}`}
      onClick={onClick}
      className={`group relative overflow-hidden ${rounded} border border-ink/10 bg-surface ${className} ${onClick ? "cursor-zoom-in" : ""}`}
    >
      <img
        decoding="async"
        src={photoUrl(photo)}
        alt={alt || photo.caption || photo.tag || "Goyal Academy"}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        className="block h-auto w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.03]"
      />
      {photo.caption && (
        <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent px-4 pb-3 pt-10 text-xs font-semibold text-cream">{photo.caption}</p>
      )}
    </div>
  );
};

export const Lightbox = ({ photos, index, onClose, onIndex, showCaptions = true }) => {
  const open = index !== null && index !== undefined && photos[index];
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % photos.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + photos.length) % photos.length);
    };
    document.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => { document.removeEventListener("keydown", onKey); window.__lenis?.start(); };
  }, [open, index, photos.length, onClose, onIndex]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
        >
          <button data-testid="lightbox-close" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-terra">
            <X className="h-5 w-5" />
          </button>
          {photos.length > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + photos.length) % photos.length); }} aria-label="Previous" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-terra sm:left-6">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % photos.length); }} aria-label="Next" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-cream/10 text-cream transition-colors hover:bg-terra sm:right-6">
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
          {isVideo(photos[index]) ? (
            <motion.video
              key={photos[index].id}
              data-testid="lightbox-video"
              src={photoUrl(photos[index])}
              poster={photos[index].poster ? posterUrl(photos[index]) : undefined}
              controls
              autoPlay
              playsInline
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] w-full max-w-5xl rounded-2xl bg-black shadow-2xl"
            />
          ) : (
          <motion.img
            key={photos[index].id}
            src={photoUrl(photos[index])}
            alt={photos[index].caption || ""}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
          />
          )}
          {((showCaptions && photos[index].caption) || photos.length > 1) && (
            <p className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-cream/10 px-4 py-1.5 text-xs font-semibold text-cream/80">
              {showCaptions && photos[index].caption ? `${photos[index].caption} · ` : ""}{index + 1} / {photos.length}
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const PhotoGrid = ({ photos, cols = "columns-2 sm:columns-3", limit, emptyLabel = "Photos coming soon", emptyRatio = "aspect-[16/7]", testid = "photo-grid", showCaptions = true }) => {
  const [idx, setIdx] = useState(null);
  const list = limit ? photos.slice(0, limit) : photos;
  if (!photos.length) return <EmptyFrame ratio={emptyRatio} label={emptyLabel} rounded="rounded-2xl" />;
  return (
    <>
      <div data-testid={testid} className={`${cols} gap-3 [&>*]:mb-3`}>
        {list.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="break-inside-avoid"
          >
            <div data-testid={`${testid}-item-${i}`} onClick={() => setIdx(i)} className="group relative cursor-zoom-in overflow-hidden rounded-2xl border border-ink/10 bg-surface">
              {isVideo(p) ? (
                <VideoTile p={p} />
              ) : (
                <img decoding="async" src={photoUrl(p)} alt={p.caption || ""} loading="lazy" className="block h-auto w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
              )}
              {showCaptions && p.caption && <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent px-3 pb-2.5 pt-8 text-[11px] font-semibold text-cream">{p.caption}</p>}
            </div>
          </motion.div>
        ))}
      </div>
      <Lightbox photos={list} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} showCaptions={showCaptions} />
    </>
  );
};
